import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiPath from '../../utils/apiPath';

// ── Fetch All (paginated + filtered) ───────────────────────────────────────
export const fetchInbounds = createAsyncThunk(
  'inbound/fetchAll',
  async (
    { page = 1, limit = 10, status = '', item_code = '', date_range = '' } = {},
    { rejectWithValue }
  ) => {
    try {
      let url = `/api/v1.0/inbound?page=${page}&limit=${limit}`;
      if (status)    url += `&status=${encodeURIComponent(status)}`;
      if (item_code) url += `&item_code=${encodeURIComponent(item_code)}`;
      if (date_range) url += `&date_range=${date_range}`;  
      const response = await apiPath.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Fetch Lists (dropdown) ─────────────────────────────────────────────────
export const fetchInboundLists = createAsyncThunk(
  'inbound/fetchLists',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiPath.get('/api/v1.0/inbound/lists');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Fetch By ID ────────────────────────────────────────────────────────────
export const fetchInboundById = createAsyncThunk(
  'inbound/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/inbound/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Create ─────────────────────────────────────────────────────────────────
export const createInbound = createAsyncThunk(
  'inbound/create',
  async (inboundData, { rejectWithValue }) => {
    try {
      const response = await apiPath.post('/api/v1.0/inbound/create', inboundData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Update ─────────────────────────────────────────────────────────────────
export const updateInbound = createAsyncThunk(
  'inbound/update',
  async ({ id, inboundData }, { rejectWithValue }) => {
    try {
      const response = await apiPath.put(`/api/v1.0/inbound/${id}`, inboundData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Delete ─────────────────────────────────────────────────────────────────
export const deleteInbound = createAsyncThunk(
  'inbound/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiPath.delete(`/api/v1.0/inbound/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Slice ──────────────────────────────────────────────────────────────────
const inboundSlice = createSlice({
  name: 'inbound',
  initialState: {
    inbounds:       [],
    inboundLists:   [],
    currentInbound: null,
    loading:        false,
    error:          null,
    totalRecord:    0,
    totalPage:      0,
    currentPage:    1,
    perPage:        10,
  },
  reducers: {
    clearInboundError:    (state) => { state.error = null; },
    setCurrentPage:       (state, action) => { state.currentPage = action.payload; },
    clearCurrentInbound:  (state) => { state.currentInbound = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInbounds.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchInbounds.fulfilled, (state, action) => {
        state.loading     = false;
        state.inbounds    = action.payload.records || [];
        state.totalRecord = action.payload.totalRecord  || 0;
        state.totalPage   = action.payload.totalPage    || 0;
        state.currentPage = action.payload.currentPage  || 1;
        state.perPage     = action.payload.perPage      || 10;
      })
      .addCase(fetchInbounds.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      .addCase(fetchInboundLists.pending,   (state) => { state.loading = true; })
      .addCase(fetchInboundLists.fulfilled, (state, action) => {
        state.loading      = false;
        state.inboundLists = action.payload.records || action.payload || [];
      })
      .addCase(fetchInboundLists.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      .addCase(fetchInboundById.pending,   (state) => { state.loading = true; })
      .addCase(fetchInboundById.fulfilled, (state, action) => {
        state.loading        = false;
        state.currentInbound = action.payload?.details || action.payload;
      })
      .addCase(fetchInboundById.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      .addCase(createInbound.pending,   (state) => { state.loading = true; })
      .addCase(createInbound.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.id) {
          state.inbounds    = [action.payload, ...state.inbounds];
          state.totalRecord = state.totalRecord + 1;
        }
      })
      .addCase(createInbound.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      .addCase(updateInbound.pending,   (state) => { state.loading = true; })
      .addCase(updateInbound.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index   = state.inbounds.findIndex((r) => r.id === updated.id);
        if (index !== -1) state.inbounds[index] = updated;
        if (state.currentInbound?.id === updated.id) state.currentInbound = updated;
      })
      .addCase(updateInbound.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      .addCase(deleteInbound.pending,   (state) => { state.loading = true; })
      .addCase(deleteInbound.fulfilled, (state, action) => {
        state.loading     = false;
        state.inbounds    = state.inbounds.filter((r) => r.id !== action.payload);
        state.totalRecord = Math.max(0, state.totalRecord - 1);
      })
      .addCase(deleteInbound.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      });
  },
});

export const { clearInboundError, setCurrentPage, clearCurrentInbound } =
  inboundSlice.actions;
export default inboundSlice.reducer;